import React, { useState, useMemo } from 'react';

// Define the Stock interface
interface Stock {
  id: number;
  symbol: string;
  name: string;
  slug: string;
  current_price?: number | string;
  pe_ratio?: number | string;
  eps?: number | string;
  dividend_yield?: number | string;
  analyst_target_price?: number | string;
}

// Define the component props interface
interface StockListProps {
  stocks?: Stock[];
}

const StockList: React.FC<StockListProps> = ({ stocks = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter stocks based on search term
  const filteredStocks = useMemo(() => {
    if (!searchTerm.trim()) return stocks;

    const term = searchTerm.toLowerCase().trim();
    return stocks.filter(
      (stock) =>
        stock.symbol.toLowerCase().includes(term) ||
        stock.name.toLowerCase().includes(term)
    );
  }, [stocks, searchTerm]);

  const formatNumber = (num?: number | string, decimals = 2) => {
    if (num === null || num === undefined || num === '') return 'N/A';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    return !isNaN(numValue) ? numValue.toFixed(decimals) : 'N/A';
  };

  const formatPrice = (price?: number | string) => {
    if (price === null || price === undefined || price === '') return 'N/A';
    const numValue = typeof price === 'string' ? parseFloat(price) : price;
    return !isNaN(numValue) ? `$${numValue.toFixed(2)}` : 'N/A';
  };

  const formatPercentage = (num?: number | string) => {
    if (num === null || num === undefined || num === '') return 'N/A';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    return !isNaN(numValue) ? `${numValue.toFixed(2)}%` : 'N/A';
  };

  return (
    <div className="stock-list-container">
      <div className="stock-list-header">
        <p className="stock-count">{filteredStocks.length} stocks found</p>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by symbol or company name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="search-icon">🔍</div>
      </div>

      {/* Stock Table */}
      <div className="table-container">
        <table className="stocks-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Company</th>
              <th>Current Price</th>
              <th>Target Price</th>
              <th>P/E Ratio</th>
              <th>EPS</th>
              <th>Dividend Yield</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredStocks.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-results">
                  {searchTerm ? 'No stocks found matching your search.' : 'No stocks available.'}
                </td>
              </tr>
            ) : (
              filteredStocks.map((stock) => {
                const currentPrice = stock.current_price;
                const targetPrice =
                  typeof stock.analyst_target_price === 'string'
                    ? parseFloat(stock.analyst_target_price)
                    : stock.analyst_target_price;

                return (
                  <tr key={stock.id} className="stock-row">
                    <td className="symbol-cell">
                      <strong>{stock.symbol}</strong>
                    </td>
                    <td className="company-cell">
                      <div className="company-name">{stock.name}</div>
                    </td>
                    <td className="price-cell">{formatPrice(currentPrice)}</td>
                    <td className="target-price-cell">
                      {targetPrice ? formatPrice(targetPrice) : 'N/A'}
                      {currentPrice && targetPrice && (
                        <div className="price-diff">
                          <span className={targetPrice > Number(currentPrice) ? 'positive' : 'negative'}>
                            ({targetPrice > Number(currentPrice) ? '+' : ''}
                            {((targetPrice - Number(currentPrice)) / Number(currentPrice) * 100).toFixed(1)}%)
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="pe-ratio-cell">{formatNumber(stock.pe_ratio)}</td>
                    <td className="eps-cell">${formatNumber(stock.eps)}</td>
                    <td className="dividend-cell">{formatPercentage(stock.dividend_yield)}</td>
                    <td className="action-cell">
                      <a href={`/stocks/${stock.slug}`} className="button outline small">
                        Details
                      </a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockList;